import { enqueueSnackbar } from 'notistack';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import {
    NewCarcinogenicReport,
    useCreateCarcinogenicReportMutation,
    useEditCarcinogenicReportEstablishmentMutation
} from '../../../../../services/carcinogenicApi';
import useAlerts from './useAlerts';
import { useTranslation } from 'react-i18next';
import { TAB_INDEX } from '../../../pages';
import { useUpdatePresentationStateMutation } from '../../../../../services/presentacionesApi';
import { PRESENTATION_STATE } from '../../../../Presentaciones/consts';
import { useNavigate } from 'react-router-dom';
import { hideAlert } from '../../../../../features/slices/applicationSlice';
import { useDispatch } from 'react-redux';
import { handleErrors } from '../../../../../utils/common';
import { setSelectedCarcinogenicReportId } from '../../../../../redux/slices/carcinogenicReportSlice';

const useEstablishmentModule = ({
    employeeQuantity,
    anotherData,
    selectedEstablishmentId,
    handleTabChange
}: any) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [createCarcinogenicReport, { isLoading: isCreatingCarcinogenicReport }] =
        useCreateCarcinogenicReportMutation();

    const [editCarcinogenicReport, { isLoading: isEditingCarcinogenicReport }] =
        useEditCarcinogenicReportEstablishmentMutation();

    const [updatePresentationState, {isLoading: isUpdatingPresentationState}] = useUpdatePresentationStateMutation();
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const {
        employeeQuantityFieldsConfig,
        formData: employeeQuantityFormData,
        validateForm: validateEmployeeQuantityForm
    } = employeeQuantity;

    const {
        anotherDataFieldsConfig,
        formData: anotherDataFormData,
        validateForm: validateAnotherDataForm
    } = anotherData;

    const { t } = useTranslation('carcinogenicReport', { keyPrefix: 'modules.establishmentData' });

    const adaptNewCarcinogenicReportBody = () => {
        return {
            idEstablecimiento: selectedEstablishmentId,
            establecimiento: {
                propiosAdministrativos: Number(employeeQuantityFormData?.ownAdministrativeWorkers),
                eventualesAdministrativos: Number(
                    employeeQuantityFormData?.eventualAdministrativeWorkers
                ),
                propiosProduccion: Number(employeeQuantityFormData?.ownProductionWorkers),
                eventualesProduccion: Number(employeeQuantityFormData?.eventualProductionWorkers),
                permiteFumarLugarCerrado: anotherDataFormData?.hasSmokinPlaces?.value,
                permiteFumar: anotherDataFormData?.isSmokingAllowed?.value,
                observacion: anotherDataFormData?.observations || ' '
            }
        };
    };

    const { showMissingRequiredFieldsAlert, showCancelCarcinogenicReportAlert } = useAlerts({ translate: t });

    const handleDeleteReport = async () => {
        try {
            if (!selectedCarcinogenicReportId)  {
                enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                    variant: 'error'
                });
                return
            }
            const response: any = await updatePresentationState({
                presentationId: selectedCarcinogenicReportId,
                body: { idEstado: PRESENTATION_STATE.CANCELADO }
            });
            if (response?.error) {
                enqueueSnackbar(t('enqueues.delete.error'), {
                    variant: 'error'
                });
                return;
            } else {
                enqueueSnackbar(t('enqueues.delete.success'), {
                    variant: 'success'
                });
                navigate(-1);
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    }

    const handleCancelPress = () => {
        showCancelCarcinogenicReportAlert({
            confirmAction: handleDeleteReport,
            cancelAction: () => {
                dispatch(hideAlert());
                navigate(-1)}
        });
    }

    const handleEditCarcinogenicReport = async (
        adaptedNewCarcinogenicReportBody: NewCarcinogenicReport
    ) => {
        try {
            if (!selectedCarcinogenicReportId) return;
            const response: any = await editCarcinogenicReport({
                reportId: selectedCarcinogenicReportId,
                body: adaptedNewCarcinogenicReportBody
            });
            if (response?.error) {
                if(response?.error?.data?.statusCode === 400) {
                    handleErrors(response?.error);
                    return;
                }
                enqueueSnackbar(t('enqueues.edit.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.edit.success'), {
                    variant: 'success'
                });
                handleTabChange(null, TAB_INDEX.SUBSTANCES);
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleCreateCarcinogenicReport = async (
        adaptedNewCarcinogenicReportBody: NewCarcinogenicReport
    ) => {
        try {
            const response: any = await createCarcinogenicReport(adaptedNewCarcinogenicReportBody);
            
            if (response?.error) {
                if(response?.error?.data?.statusCode === 400) {
                    handleErrors(response?.error);
                    return;
                }
                enqueueSnackbar(t('enqueues.create.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.create.success'), {
                    variant: 'success'
                });
                dispatch(setSelectedCarcinogenicReportId(response?.data?.data));
                handleTabChange(null, TAB_INDEX.SUBSTANCES);
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleSubmit = async () => {
        if (!selectedEstablishmentId) return;
        if (!validateEmployeeQuantityForm(employeeQuantityFormData, employeeQuantityFieldsConfig)) {
            showMissingRequiredFieldsAlert();
            return;
        }
        if (!validateAnotherDataForm(anotherDataFormData, anotherDataFieldsConfig)) {
            showMissingRequiredFieldsAlert();
            return;
        }

        try {
            const adaptedNewCarcinogenicReportBody = adaptNewCarcinogenicReportBody();
            if (selectedCarcinogenicReportId) {
                handleEditCarcinogenicReport(adaptedNewCarcinogenicReportBody);
            } else {
                handleCreateCarcinogenicReport(adaptedNewCarcinogenicReportBody);
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    return {
        isCreatingCarcinogenicReport,
        isEditingCarcinogenicReport,
        isUpdatingPresentationState,
        handleCancelPress,
        handleSubmit
    };
};

export default useEstablishmentModule;
